/**
 *
 *
 * @param name
 * @param type
 * @param content
 * @param roles
 * @returns {Promise<void>}
 */
import {apiDELETE, apiGET, apiGETPaginated, apiPOST, apiPUT} from "./RequestsCore";


/**
 * Gets the requested expedient based on provided expedient_id.
 *
 * @param expedient_id
 * @param without_images_base64
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function getExpedient(expedient_id, without_images_base64) {
    let data = {};

    if (without_images_base64) {
        data.without_images_base64 = without_images_base64;
    }

    return await apiGET('/api/expedient/' + expedient_id, data);
}

/**
 * Returns the existing expedients according to provided filters, without pagination.
 *
 * @param filters
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getExpedients(filters) {
    return await apiGET('/api/expedients', filters);
}

/**
 * Gets all the existing expedients according to provided filters, with pagination.
 *
 * @param filters
 * @param cancelToken
 * @returns {Promise<AxiosResponse<*>|*|null>}
 */
export async function getExpedientsPaginated(filters, cancelToken) {
    return await apiGETPaginated('/api/expedients', filters, cancelToken);
}

/**
 * Gets all the existing expedients for the provided client.
 *
 * @param client_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getClientExpedients(client_id) {
    return await apiGET('/api/client/' + client_id + '/expedients');
}

/**
 * Gets all the existing expedients for the provided entity.
 *
 * @param entity_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getEntityExpedients(entity_id) {
    return await apiGET('/api/entity/' + entity_id + '/expedients');
}

/**
 * Gets the entity related with the given expedient
 * 
 * @param expedient_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getExpedientEntity(expedient_id) {
    return await apiGET('/api/expedient/' + expedient_id + '/entity');
}

/**
 * Gets all the existing expedients for the provided client and entity.
 *
 * @param client_id
 * @param entity_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getClientAndEntityExpedients(client_id, entity_id) {
    return await apiGET('/api/expedients/client/' + client_id + '/entity/' + entity_id);
}

/**
 * Creates a new expedient with the provided entity, client, name, contract type and responsible.
 *
 * @param name
 * @param entity_id
 * @param client_id
 * @param contract_type
 * @param responsible
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function createExpedient(name, entity_id, client_id, contract_type = 'mortgage', responsible = 'atencion_al_cliente') {
    let data = {
        name: name,
        entity_id: entity_id,
        client_id: client_id,
        contract_type: contract_type,
        responsible: responsible
    }

    return await apiPOST('/api/expedient', data);
}

/**
 * Returns all the existing expedient responsible.
 *
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getResponsibles() {
    return await apiGET('/api/expedient/responsibles');
}

/**
 * Returns all the existing expedient contract types.
 *
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function getContractTypes() {
    return await apiGET('/api/expedient/contract-types');
}

/**
 * Updates the provided expedient fields (name, type and/or doc_status).
 *
 * @param expedient_id
 * @param name
 * @param type
 * @param responsible
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function updateExpedient(expedient_id, name = null, type = null, responsible = null) {
    let data = {}

    if (name !== null) {
        data.name = name;
    }
    if (type !== null) {
        data.type = type;
    }
    if (responsible !== null) {
        data.responsible = responsible;
    }

    return await apiPUT('/api/expedient/' + expedient_id, data);
}

/**
 * Deletes the previous client and stores the new one.
 *
 * @param expedient_id
 * @param client_id
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function updateClient(expedient_id, client_id) {
    return await apiPOST('/api/expedient/' + expedient_id + '/client/' + client_id);
}

/**
 * Deletes the previous entity and stores the new one.
 *
 * @param expedient_id
 * @param entity_id
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function updateEntity(expedient_id, entity_id) {
    return await apiPOST('/api/expedient/' + expedient_id + '/entity/' + entity_id);
}

/**
 * Updates the requested expedient with the provided responsible.
 *
 * @param expedient_id
 * @param responsible
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function updateExpedientResponsible(expedient_id, responsible) {
    return await updateExpedient(expedient_id, null, null, responsible);
}

/**
 * Updates the requested expedient with the provided contract type.
 *
 * @param expedient_id
 * @param contract_type
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function updateContractType(expedient_id, contract_type) {
    let data = {
        contract_type: contract_type,
    }
    return await apiPUT('/api/expedient/' + expedient_id + '/change-contract-type', data);

}

/**
 * Updates the requested expedient with the provided name.
 *
 * @param expedient_id
 * @param client_id
 * @param data
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function updateExpedientName(expedient_id, client_id, data) {
    return await apiPUT('/api/expedient/' + expedient_id + '/' + client_id +'/change-name', data);
}

/**
 * Sets the provided attorney as the related to expedient, deleting the previous one if existing.
 *
 * @param expedient_id
 * @param attorney_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function setRelatedAttorney(expedient_id, attorney_id) {
    return apiPOST('/api/expedient/' + expedient_id + '/attorney/' + attorney_id);
}

/**
 * Sets the provided lawyer as the related to expedient, deleting the previous one if existing.
 *
 * @param expedient_id
 * @param lawyer_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function setRelatedLawyer(expedient_id, lawyer_id) {
    return apiPOST('/api/expedient/' + expedient_id + '/lawyer/' + lawyer_id);
}

/**
 *  * Deletes the provided expedient
 *
 * @param expedient_id
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function deleteExpedient(expedient_id) {
    return await apiDELETE('/api/expedient/' + expedient_id);
}

/**
 * Attaches two existing expedients
 *
 * @param expedient_id
 * @param related_expedient_id
 * @returns {Promise<T|AxiosResponse<*>|*|null>}
 */
export async function attachExpedient(expedient_id, related_expedient_id) {
    let data = {
        related_expedient_id: related_expedient_id,
    }

    return await apiPOST('/api/expedient/' + expedient_id + '/attach', data);
}
